import React, { useEffect, useState } from 'react';
import {
  Box,
  HeadingText,
  GDSStyleObject,
  StackLayout,
  ParagraphText,
  Checkbox,
  Fieldset,
  Radio,
  FormField,
} from '@leagueplatform/genesis-core';
import { isEmpty, isNotEmpty, updateFormData } from '../../../helpers';
import { IS_POLICY_HOLDER_PATIENT } from '../../../../../types/online-forms.types';

export function OtherCoverageInformation(props: any) {
  const { errorHandling, nextButtonClicked, footerToBottom } = props;

  const [form, setForm] = useState({
    hasDentalCoverage: false,
    hasMedicalCoverage: false,
    notApplicable: false,
    primaryInsurance: '',
  });

  const [isOtherCoverageError, setIsOtherCoverageError] = useState(false);
  const [otherCoverageError, setOtherCoverageError] = useState('');

  const [isPrimaryInsuranceError, setIsPrimaryInsuranceError] = useState(false);
  const [primaryInsuranceError, setPrimaryInsuranceError] = useState('');

  const isValid =
    form.notApplicable ||
    (form.hasDentalCoverage && isNotEmpty(form.primaryInsurance)) ||
    (form.hasMedicalCoverage && isNotEmpty(form.primaryInsurance));
  useEffect(() => {
    footerToBottom(false);
  }, [footerToBottom]);
  useEffect(() => {
    if (form.notApplicable) {
      errorHandling(true);
    } else if (form.hasDentalCoverage || form.hasMedicalCoverage) {
      if (form.primaryInsurance) {
        errorHandling(true);
      } else {
        errorHandling(false);
      }
    } else {
      errorHandling(false);
    }
  }, [errorHandling, form]);

  useEffect(() => {
    if (nextButtonClicked) {
      if (!isValid) {
        if (
          !form.hasDentalCoverage &&
          !form.hasMedicalCoverage &&
          !form.notApplicable
        ) {
          setIsOtherCoverageError(true);
          setOtherCoverageError('This selection is required.');
        } else if (form.hasDentalCoverage || form.hasMedicalCoverage) {
          if (isEmpty(form.primaryInsurance)) {
            setIsOtherCoverageError(false);
            setOtherCoverageError('');
            setIsPrimaryInsuranceError(true);
            setPrimaryInsuranceError('This selection is required.');
          } else {
            setIsOtherCoverageError(false);
            setOtherCoverageError('');
            setIsPrimaryInsuranceError(false);
            setPrimaryInsuranceError('');
          }
        } else {
          setIsOtherCoverageError(false);
          setOtherCoverageError('');
          setIsPrimaryInsuranceError(false);
          setPrimaryInsuranceError('');
        }
      }
    }
  }, [form, isValid, nextButtonClicked]);

  const clearOtherCoverageDetails = () => {
    setForm((prevState) => ({
      ...prevState,
      policyHolderName: '',
      policyHolderDob: '',
      policyHolderGender: '',
      policyID: '',
      planNumber: '',
      relationshipToPolicyHolder: '',
      otherInsuranceCompany: '',
      otherInsuranceAddressLine1: '',
      otherInsuranceAddressLine2: '',
      otherInsuranceAddressCity: '',
      otherInsuranceAddressState: '',
      otherInsuranceAddressZipCode: '',
      eobAttachments: [],
    }));
  };

  const onUpdate = (e: any) => {
    const { checked, id, value } = e.target;
    if (id === 'hasDentalCoverage' || id === 'hasMedicalCoverage') {
      let nextFormState = {
        ...form,
        [id]: checked,
        notApplicable: false,
      };
      if (
        !nextFormState.hasDentalCoverage &&
        !nextFormState.hasMedicalCoverage
      ) {
        nextFormState = {
          ...nextFormState,
          primaryInsurance: '',
        };
      }
      setForm(nextFormState);
      setIsOtherCoverageError(false);
      setOtherCoverageError('');
    } else if (id === 'notApplicable') {
      const nextFormState = {
        ...form,
        [id]: checked,
        hasDentalCoverage: false,
        hasMedicalCoverage: false,
        primaryInsurance: '',
      };
      setForm(nextFormState);
      setIsOtherCoverageError(false);
      setOtherCoverageError('');
    }
    if (id === 'primaryInsurance-1' || id === 'primaryInsurance-2') {
      const nextFormState = {
        ...form,
        primaryInsurance: value,
      };
      setForm(nextFormState);
      setIsPrimaryInsuranceError(false);
      setPrimaryInsuranceError('');
    }
    if (id === 'notApplicable' || id === 'primaryInsurance-2') {
      clearOtherCoverageDetails();
    }
  };

  useEffect(() => {
    const pInfo = JSON.parse(sessionStorage.getItem('formData') || '{}');
    if (pInfo) {
      setForm({
        hasDentalCoverage: pInfo?.hasDentalCoverage || false,
        hasMedicalCoverage: pInfo?.hasMedicalCoverage || false,
        notApplicable: pInfo?.notApplicable || false,
        primaryInsurance: pInfo?.primaryInsurance || '',
      });
    }
  }, []);

  useEffect(() => {
    if (form) {
      updateFormData(form);
    }
  }, [form]);

  return (
    <StackLayout
      css={
        {
          backgroundColor: 'White',
          maxWidth: '800px',
        } as GDSStyleObject
      }
      horizontalAlignment="center"
    >
      <Box
        css={
          {
            display: 'flex',
            flexDirection: 'column',
            padding: '$three $two $two $two',
          } as GDSStyleObject
        }
      >
        <Box
          css={
            {
              display: 'flex',
              flexDirection: 'column',
              padding: '$none $two',
            } as GDSStyleObject
          }
        >
          <HeadingText
            level="1"
            size={{
              '@initial': 'xxl',
              '@mobile': 'xl',
              '@mobileLandscape': 'xl',
              '@tablet': 'xl',
              '@laptop': 'xxl',
              '@desktop': 'xxl',
            }}
          >
            Member Dental Claim Form
          </HeadingText>
        </Box>
        <Box
          css={
            {
              display: 'flex',
              flexDirection: 'column',
              padding: '$one $two',
              width: '100%',
            } as GDSStyleObject
          }
        >
          <Box
            css={
              {
                display: 'flex',
                flexDirection: 'column',
              } as GDSStyleObject
            }
          >
            <HeadingText
              level="3"
              data-testid="step4"
              size={{
                '@initial': 'xl',
                '@mobile': 'lg',
                '@mobileLandscape': 'lg',
                '@tablet': 'xl',
                '@laptop': 'xl',
                '@desktop': 'xl',
              }}
            >
              Step 4: Other Coverage
            </HeadingText>
            <ParagraphText
              css={
                {
                  marginTop: '$one',
                } as GDSStyleObject
              }
            >
              <p>
                If the patient has other coverage through a spouse or domestic
                partner, or if the patient is a minor dependent, list the
                primary policy holder’s details from the other coverage.
              </p>
            </ParagraphText>
          </Box>
          <Box
            css={
              {
                marginBottom: '$one',
              } as GDSStyleObject
            }
          >
            <HeadingText
              level="5"
              size={{
                '@initial': 'xl',
                '@mobile': 'lg',
                '@mobileLandscape': 'lg',
                '@tablet': 'xl',
                '@laptop': 'xl',
                '@desktop': 'xl',
              }}
            >
              Do you have any other insurance coverage? Select all that apply.
            </HeadingText>
            <Box
              css={{
                paddingTop: '$threeQuarters',
              }}
            >
              <Fieldset
                css={
                  {
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '$one $none',
                  } as GDSStyleObject
                }
                legend={undefined}
                inputStatus={isOtherCoverageError ? 'error' : undefined}
                statusMessage={otherCoverageError}
                id="otherCoverage"
              >
                <Checkbox
                  id="hasDentalCoverage"
                  name="hasDentalCoverage"
                  label="Dental"
                  checked={form.hasDentalCoverage}
                  onChange={(e) => {
                    onUpdate(e);
                    sessionStorage.setItem('otherCoverage', '');
                  }}
                />
                <Checkbox
                  id="hasMedicalCoverage"
                  name="hasMedicalCoverage"
                  label="Medical"
                  checked={form.hasMedicalCoverage}
                  onChange={(e) => {
                    onUpdate(e);
                    sessionStorage.setItem('otherCoverage', '');
                  }}
                />
                <Checkbox
                  id="notApplicable"
                  name="notApplicable"
                  label="N/A"
                  checked={form.notApplicable}
                  onChange={(e) => {
                    onUpdate(e);
                    sessionStorage.setItem('otherCoverage', 'NO');
                  }}
                />
              </Fieldset>
            </Box>
          </Box>
          {(form.hasDentalCoverage || form.hasMedicalCoverage) && (
            <>
              <Box>
                <HeadingText
                  level="3"
                  size={{
                    '@initial': 'xl',
                    '@mobile': 'lg',
                    '@mobileLandscape': 'lg',
                    '@tablet': 'xl',
                    '@laptop': 'xl',
                    '@desktop': 'xl',
                  }}
                >
                  Is this the primary insurance for the patient?
                </HeadingText>
                <FormField
                  id="primaryInsurance"
                  label=""
                  statusIconLabel="Error"
                  inputStatus={isPrimaryInsuranceError ? 'error' : undefined}
                  statusMessage={
                    isPrimaryInsuranceError ? primaryInsuranceError : ''
                  }
                >
                  <Fieldset
                    css={
                      {
                        display: 'flex',
                        maxWidth: '436px',
                        paddingTop: '$half',
                      } as GDSStyleObject
                    }
                    legend={undefined}
                  >
                    <Radio
                      id="primaryInsurance-1"
                      name="primaryInsurance-1"
                      data-testid="primaryInsurance1"
                      checked={form.primaryInsurance === 'Yes'}
                      value={IS_POLICY_HOLDER_PATIENT.YES}
                      label={IS_POLICY_HOLDER_PATIENT.YES}
                      onChange={(e) => {
                        onUpdate(e);
                        sessionStorage.setItem('otherCoverage', 'YES');
                      }}
                    />
                    <Radio
                      id="primaryInsurance-2"
                      name="primaryInsurance-2"
                      data-testid="primaryInsurance2"
                      checked={form.primaryInsurance === 'No'}
                      value={IS_POLICY_HOLDER_PATIENT.NO}
                      label={IS_POLICY_HOLDER_PATIENT.NO}
                      onChange={(e) => {
                        onUpdate(e);
                        sessionStorage.setItem('otherCoverage', 'NO');
                      }}
                    />
                  </Fieldset>
                </FormField>
              </Box>
              {form.primaryInsurance === 'Yes' && (
                <Box>
                  <ParagraphText
                    css={
                      {
                        marginTop: '$two',
                      } as GDSStyleObject
                    }
                  >
                    <p>
                      If you have marked both Dental and Medical, please fill
                      out the details on the next few pages for other{' '}
                      <span style={{ fontWeight: 'bold' }}>dental</span>{' '}
                      insurance only. When prompted, please attach the
                      Explanation of Benefits documentation from any other
                      insurance policy.
                    </p>
                  </ParagraphText>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </StackLayout>
  );
}
